'use client';
import React, { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { useConfig } from '@/contexts/ConfigContext';

import { useAppDispatch, useAppSelector, useAppStore } from '@/lib/hooks';
import { setDarkMode, setSidenavMinimized, setSidenavVisible } from '@/lib/slices/nav-slice';
import { setisLoggedIn, setRole } from '@/lib/slices/login-slice';

import ProgressNav from './ProgressNav';
import Link from 'next/link';
import Button from './ui/Button';
import { SunIcon, MoonIcon } from '@/assets/ui/theme';
import { ChevronDownIcon, ChevronUpIcon } from '@/assets/icons/chevron';
import { CircleUserIcon, LifeBuoy } from 'lucide-react';
import { SideListIcon } from '@/assets/icons';
import { ExitIcon } from '@/assets/nav';

import { NavItem, NavItemHideList, NavItemMap } from '@/services/nav/nav-item-map';
import SiteThemCacheService from '@/services/storage/site-theme-cache-service';
import AuthService from '@/services/auth/auth-service';
import { admin_role } from 'auth-types';
import { CourseId } from 'course-types';

function SideNav(): React.JSX.Element | null {
  const config = useConfig();
  const router = useRouter();
  const pathname = usePathname();
  const store = useAppStore();
  const dispatch = useAppDispatch();

  const {
    status: loggedIn,
    role: userRole,
    admin_role: adminRole,
    first_name,
    last_name,
    email,
  } = useAppSelector((state) => state.isLoggedIn);
  const name = `${first_name} ${last_name}`;

  const visible = useAppSelector(() => store.getState().navigation.visible);
  const hidden = useAppSelector(() => store.getState().navigation.minimized);
  const darkMode: boolean = useAppSelector(() => store.getState().navigation.darkMode);
  const activeStudentCourse = useAppSelector(() => store.getState().course.activeCourseData);
  const currentActivity = useAppSelector(() => store.getState().activity);

  const [themeCached, setThemeCached] = useState<boolean>(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const isActiveRoute = (route: string): boolean => pathname === `${route}`;

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!loggedIn || isActiveRoute('')) {
      dispatch(setSidenavVisible(false));
      dispatch(setSidenavMinimized(true));
    } else {
      dispatch(setSidenavMinimized(false));
    }
  }, [loggedIn, pathname]);

  // Close mobile sidenav when route changes
  useEffect(() => {
    if (isMobile && visible) {
      dispatch(setSidenavVisible(false));
    }
  }, [pathname, isMobile]);

  // Prevent body scroll when mobile sidenav is open
  useEffect(() => {
    if (isMobile && visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobile, visible]);

  // Set color theme for site
  useEffect(() => {
    if (darkMode) {
      document.body.classList.add('dark');
    } else {
      document.body.classList.remove('dark');
    }
    if (themeCached) SiteThemCacheService.setThemeCookie({ darkMode: darkMode });
  }, [darkMode]);

  useEffect(() => {
    if (loggedIn) {
      const theme = SiteThemCacheService.getThemeCookie();
      if (theme) {
        dispatch(setDarkMode(theme.darkMode));
      } else {
        SiteThemCacheService.setThemeCookie({ darkMode: darkMode });
      }
      setThemeCached(true);
    }
  }, [loggedIn]);

  const signOut = () => {
    const callback = () => {
      dispatch(setRole(''));
      dispatch(setisLoggedIn(false));
      dispatch(setDarkMode(false));
    };
    AuthService.signOut(router, callback);
  };

  const toggleSidenav = () => {
    if (isMobile) {
      dispatch(setSidenavVisible(!visible));
      dispatch(setSidenavMinimized(false));
    } else {
      dispatch(setSidenavVisible(!visible));
    }
  };

  const closeMobileSidenav = () => {
    if (isMobile) {
      dispatch(setSidenavVisible(false));
    }
  };

  const onSideNavItemClick = (item: NavItem) => {
    if (item.subItems) {
      toggleDropdown(item.name);
      return;
    }
    router.push(item.route);
    // Close mobile sidenav after navigation
    if (isMobile) {
      dispatch(setSidenavVisible(false));
    }
  };

  const toggleDropdown = (name: string) => {
    if (!visible) {
      toggleSidenav();
    }
    setOpenDropdown((prevDropdown) => (prevDropdown === name ? null : name));
  };

  const itemDisabled = (item: NavItem): boolean => {
    if (item.route.endsWith('/student/course') && activeStudentCourse.id == ('' as CourseId)) {
      return true;
    }
    return false;
  };

  const getNavItems = () => {
    if (userRole === '') return [];

    return NavItemMap[`${userRole}`].map((item) => {
      if (
        !NavItemHideList[`${userRole}`].includes(item.name) &&
        (!item?.adminAccess || item?.adminAccess.includes(adminRole as admin_role))
      ) {
        const isParentActive =
          isActiveRoute(`${item.route}/`) ||
          (item.subItems && item.subItems.some((sub) => isActiveRoute(`${sub.route}/`)));
        return (
          <React.Fragment key={item.name}>
            <li
              onClick={itemDisabled(item) ? () => {} : () => onSideNavItemClick(item)}
              className={`w-full group flex flex-row dropdown-toggle ${visible ? 'my-1 px-2 gap-2 clickable' : 'clickable-icon'}${itemDisabled(item) ? '-disabled' : ''} ${isParentActive ? 'active' : ''}`}
            >
              {item?.icon && <item.icon className={`w-6 h-6`} />}
              <span className={`text-inherit ${visible ? '' : 'hidden'}`}>{item.name}</span>
              <ChevronDownIcon
                className={`w-4 ml-auto text-text_gray_400 ${!openDropdown && item.subItems && visible ? 'block' : 'hidden'}`}
              />
              <ChevronUpIcon
                className={`w-4 ml-auto text-text_gray_400 ${openDropdown && item.subItems && visible ? 'block' : 'hidden'}`}
              />
            </li>
            {visible && getNavSubItems(item)}
          </React.Fragment>
        );
      }
      return null;
    });
  };

  const getNavSubItems = (item: NavItem) => {
    if (openDropdown !== item.name || !item.subItems) {
      return null;
    }

    return (
      <ul>
        {item.subItems.map((sub) => {
          if (
            !NavItemHideList[`${userRole}`].includes(sub.name) &&
            (!sub?.adminAccess || sub?.adminAccess.includes(adminRole as admin_role))
          ) {
            return (
              <li
                key={sub.name}
                onClick={() => {
                  router.push(sub.route);
                  if (isMobile) {
                    dispatch(setSidenavVisible(false));
                  }
                }}
                className={`clickable my-2 px-2 ml-4 ${isActiveRoute(`${sub.route}/`) ? 'active' : ''}`}
              >
                {sub.name}
              </li>
            );
          }
          return null;
        })}
      </ul>
    );
  };

  const sideNavToggle = () => {
    return (
      <a
        onClick={toggleSidenav}
        className={`text-text_gray_500 hover:text-text_gray_700 ${visible ? '' : 'self-center'}`}
      >
        <SideListIcon className='flex justify-center items-center' />
      </a>
    );
  };

  const renderLogOut = () => {
    return (
      <>
        <ul className='mb-2 px-3'>
          {userRole !== 'student' && (
            <Link
              href='https://assistiv.site/'
              target='_blank'
              rel='noopener noreferrer'
              aria-label='Open Assistiv Support documentation in a new tab'
              className='flex group clickable items-center gap-2 w-full h-fit text-inherit no-underline'
            >
              <li
                className={`w-full group flex flex-row ${visible ? 'my-1 px-2 gap-2 clickable' : 'clickable-icon'}`}
              >
                <LifeBuoy className='w-6 h-6' strokeWidth={1.5} />
                <span className={`text-inherit ${visible ? '' : 'hidden'}`}>Support</span>
              </li>
            </Link>
          )}
          <li
            onClick={signOut}
            className={`w-full group flex flex-row dropdown-toggle ${visible ? 'my-2 px-2 gap-2 clickable' : 'clickable-icon'}`}
          >
            <ExitIcon className={`w-6 h-6`} />
            <span className={`text-inherit ${visible ? '' : 'hidden'}`}>{'Log Out'}</span>
          </li>
        </ul>
      </>
    );
  };

  const renderProfileLogout = () => {
    if (userRole !== 'student') return renderLogOut();
    return (
      <div className={` ${visible ? 'px-4 py-8' : 'px-3'}`}>
        {visible && (
          <div className='py-6' role='separator'>
            <hr className='w-full border-gray-200' />
          </div>
        )}
        <ul
          className={`${visible ? 'flex flex-row px-2 items-center' : 'flex flex-col items-center gap-3'}`}
        >
          <Link
            href='/student/profile'
            title={`Profile`}
            aria-label={`View profile for ${name}`}
            className={`no-underline text-inherit rounded ${visible ? 'flex-1 min-w-0' : 'size-10'} hover:opacity-100`}
          >
            <li
              className={`group flex items-center ${visible ? 'flex-row gap-3' : 'clickable-icon w-10'} rounded [&.clickable-icon]:my-0`}
            >
              <CircleUserIcon
                className={`${visible ? 'flex-shrink-0 size-10' : 'size-6'} transform transition-[width,height] duration-300 ease-in-out`}
                strokeWidth={1.25}
                aria-hidden='true'
              />
              <div className={`${visible ? 'flex flex-col min-w-0' : 'hidden'}`}>
                <span className='font-semibold text-sm truncate'>{name}</span>
                <span className='text-xs text-gray-500 truncate'>{email}</span>
              </div>
            </li>
          </Link>
          <Button
            onClick={signOut}
            title='Log out'
            aria-label='Log out of your account'
            className={`${visible ? 'h-6 w-6 min-w-6' : 'h-10 w-10 mb-4'} relative rounded hover:opacity-100 hover:text-failure`}
            text
          >
            <li
              className={`w-fit absolute ${visible ? 'dropdown-toggle top-[-12px] right-[-12px] p-3 cursor-pointer hover:translate-x-1 transform ' : 'clickable-icon top-0 right-0'} rounded [&.clickable-icon]:my-0`}
              style={{
                transition:
                  'top 300ms ease-in-out, right 300ms ease-in-out, transform 150ms ease-out',
              }}
            >
              <ExitIcon
                className={`${visible ? 'rotate-180 ' : ''} size-6 transform transition-transform duration-300 ease-in-out`}
                aria-hidden='true'
              />
              <span className='sr-only'>Log Out</span>
            </li>
          </Button>
        </ul>
      </div>
    );
  };

  if (!loggedIn) return null;
  const Logo = config?.logo;

  return (
    <>
      {/* Mobile Modal Overlay */}
      {isMobile && visible && (
        <div
          className='fixed inset-0 bg-background_gray_50 z-40 h-[calc(100vh)]'
          onClick={closeMobileSidenav}
        />
      )}

      <div
        className={`sidebar 
          flex-none h-full transition-all duration-300 ease-in-out
          ${isMobile && visible ? 'fixed inset-4 w-auto max-w-[260px] mx-auto h-[calc(95vh)]' : 'fixed top-0 left-0 h-full'}
          z-50 max-h-screen overflow-y-auto
          ${visible ? (isMobile ? '' : activeStudentCourse.id ? 'max-w-xs w-[280px]' : 'max-w-xs w-[260px]') : isMobile ? 'w-0' : 'w-16'}
          ${visible ? 'visible' : ''}
          ${isMobile ? '' : 'border-r-2 border-r-border_sidenav'}
          ${isMobile && visible ? 'bg-surface_white rounded-lg shadow-lg' : 'bg-sidenav_white'}`}
      >
        {currentActivity.id && userRole === 'student' ? (
          <nav className={`flex flex-col h-full justify-between ${visible ? '' : 'pt-4'}`}>
            <div className='flex flex-col px-3 flex-1 min-h-0'>
              {!visible && sideNavToggle()}
              {visible && <ProgressNav onClick={toggleSidenav} />}
            </div>
            {renderProfileLogout()}
          </nav>
        ) : (
          <nav className={`flex flex-col h-full w-full `}>
            <div className={`my-4 flex flex-row justify-between px-5`}>
              {sideNavToggle()}
              {visible && (
                <div className='flex items-center gap-3'>
                  <div
                    className='flex flex-row text-text_gray_500 hover:cursor-pointer hover:text-text_gray_700'
                    onClick={() => dispatch(setDarkMode(!darkMode))}
                  >
                    {!darkMode ? <SunIcon className='w-6 h-6' /> : <MoonIcon className='w-6 h-6' />}
                  </div>
                  {/* Mobile close button */}
                  {isMobile && (
                    <button
                      onClick={closeMobileSidenav}
                      className='p-1 rounded-md hover:bg-gray-100 transition-colors'
                      aria-label='Close navigation menu'
                    >
                      <svg
                        className='w-5 h-5'
                        fill='none'
                        stroke='currentColor'
                        viewBox='0 0 24 24'
                      >
                        <path
                          strokeLinecap='round'
                          strokeLinejoin='round'
                          strokeWidth={2}
                          d='M6 18L18 6M6 6l12 12'
                        />
                      </svg>
                    </button>
                  )}
                </div>
              )}
            </div>
            {visible && (
              <>
                {typeof Logo === 'function' ? (
                  <Logo
                    className={`${isMobile ? 'w-32 mt-4' : 'w-44 mt-10'} text-black dark:text-white mx-auto self-center flex px-3`}
                  />
                ) : (
                  <img
                    src={Logo?.src}
                    className={`${isMobile ? 'w-32 mt-4' : 'w-44 mt-10'} mx-auto self-center flex px-3`}
                    alt='Logo'
                  />
                )}
              </>
            )}
            <ul className='mt-4 flex-grow px-3'>{getNavItems()}</ul>
            {renderProfileLogout()}
          </nav>
        )}
      </div>
    </>
  );
}

export default SideNav;
